import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

/**
 * Remembers the page a user asked for when a guard has to send them somewhere else first,
 * so that a pasted deep link such as
 * /dashboard/product-search?productIds=1234567,1234578
 * survives the login flow and the outlet selection.
 *
 * sessionStorage is used because logging in reloads the tab, which clears everything held
 * in memory, and because it is already how the user and the outlet are kept.
 */
@Injectable({
  providedIn: 'root',
})
export class RedirectUrlService {
  private readonly storageKey = 'presto-redirect-url';

  constructor(private router: Router) {}

  /**
   * Stores the url the router is navigating to, query params included. Guards run while
   * that navigation is still in flight, which is why it is read from the current navigation
   * rather than from router.url, which still points at the previous page.
   */
  rememberCurrentNavigation(): void {
    const navigation = this.router.getCurrentNavigation();
    const url = navigation
      ? this.router.serializeUrl(navigation.extractedUrl)
      : this.router.url;

    if (!this.isInternalUrl(url)) return;

    sessionStorage.setItem(this.storageKey, url);
  }

  /**
   * Returns the remembered url once and forgets it, so it cannot send the user back there
   * a second time.
   */
  consume(): string | null {
    const url = sessionStorage.getItem(this.storageKey);

    sessionStorage.removeItem(this.storageKey);

    return url && this.isInternalUrl(url) ? url : null;
  }

  /**
   * Only paths within this app are acceptable. "//example.com" is a valid url for the
   * browser and would send the user off site.
   */
  private isInternalUrl(url: string): boolean {
    return url.startsWith('/') && !url.startsWith('//');
  }
}
